import { ParsedRecord } from 'src/components/import/ImportParser';
import { PjmLocationBulkUpdateCsvColumns } from './pjmLocationBulkUpdateParser';
import { PjmLocationDto, UpdatePjmLocationDto } from '../dto/PjmLocationDto';
import { PjmLoadReductionType } from '../dto/enums';
import { calcMaxLoadReductionCapability } from '../calcMaxLoadReductionCapability';
import { makeGeneratorInfo } from './makeGeneratorInfo';
import { makeBatteryInfo } from './makeBatteryInfo';

export function mapCsvBulkUpdateToUpdateLocationDto(
    record: ParsedRecord<PjmLocationBulkUpdateCsvColumns>,
    location: PjmLocationDto
): { uuid: string; dto: UpdatePjmLocationDto } {
    const uuid = location.uuid;
    const dto = {} as UpdatePjmLocationDto;

    // Build a lookup of existing reduction values, keyed by type
    const existingReductionMap = new Map<PjmLoadReductionType, number>(
        (location.reductionInfo ?? []).map(r => [r.reductionType!, r.reductionValue ?? 0])
    );

    // For each reduction type: use the CSV value when provided, otherwise keep the existing one
    function mergeReduction(csvValue: number | undefined, type: PjmLoadReductionType): number {
        return csvValue !== undefined ? csvValue : (existingReductionMap.get(type) ?? 0);
    }

    const csvManufacturing: number | undefined = record['Manufacturing (kW)'].parsedValue;
    const csvHvac: number | undefined = record['HVAC (kW)'].parsedValue;
    const csvGenerator: number | undefined = record['Generator (kW)'].parsedValue;
    const csvBatteries: number | undefined = record['Batteries (kW)'].parsedValue;
    const csvLighting: number | undefined = record['Lighting (kW)'].parsedValue;
    const csvWaterHeaters: number | undefined = record['Water Heaters (kW)'].parsedValue;
    const csvRefrigeration: number | undefined = record['Refrigeration (kW)'].parsedValue;
    const csvPlugLoad: number | undefined = record['Plug Load (kW)'].parsedValue;
    const csvComputing: number | undefined = record['Computing (kW)'].parsedValue;
    let csvGeneratorCount: number | undefined = record['Number of Generators'].parsedValue;

    dto.reductionInfo = [
        { reductionType: PjmLoadReductionType.MANUFACTURING, reductionValue: mergeReduction(csvManufacturing, PjmLoadReductionType.MANUFACTURING) },
        { reductionType: PjmLoadReductionType.HVAC, reductionValue: mergeReduction(csvHvac, PjmLoadReductionType.HVAC) },
        { reductionType: PjmLoadReductionType.GENERATOR, reductionValue: mergeReduction(csvGenerator, PjmLoadReductionType.GENERATOR) },
        { reductionType: PjmLoadReductionType.BATTERIES, reductionValue: mergeReduction(csvBatteries, PjmLoadReductionType.BATTERIES) },
        { reductionType: PjmLoadReductionType.LIGHTING, reductionValue: mergeReduction(csvLighting, PjmLoadReductionType.LIGHTING) },
        { reductionType: PjmLoadReductionType.WATER_HEATERS, reductionValue: mergeReduction(csvWaterHeaters, PjmLoadReductionType.WATER_HEATERS) },
        { reductionType: PjmLoadReductionType.REFRIGERATION, reductionValue: mergeReduction(csvRefrigeration, PjmLoadReductionType.REFRIGERATION) },
        { reductionType: PjmLoadReductionType.PLUG_LOAD, reductionValue: mergeReduction(csvPlugLoad, PjmLoadReductionType.PLUG_LOAD) },
        { reductionType: PjmLoadReductionType.COMPUTING, reductionValue: mergeReduction(csvComputing, PjmLoadReductionType.COMPUTING) },
    ];

    if (csvGenerator !== undefined || csvGeneratorCount !== undefined) {
        dto.generatorCount = csvGeneratorCount != null ? csvGeneratorCount : location.generatorCount || 1;
        const generatorKw = mergeReduction(csvGenerator, PjmLoadReductionType.GENERATOR);

        dto.generatorInfo = makeGeneratorInfo({
            current: {
                generatorCount: location.generatorCount || 0,
                generatorInfo: location.generatorInfo || [],
            },
            update: {
                generatorCount: dto.generatorCount,
                generatorKw: generatorKw,
            },
        });
    } else {
        dto.generatorInfo = location.generatorInfo;
        dto.generatorCount = location.generatorCount;
    }

    if (csvBatteries !== undefined) {
        // Batteries field changed in CSV — rebuild battery info from merged value
        dto.batteryInfo = makeBatteryInfo({
            current: { batteryInfo: location.batteryInfo || [] },
            update: {
                batteryKw: csvBatteries,
            },
        });
    } else {
        dto.batteryInfo = location.batteryInfo;
    }

    dto.profileInfo = {
        peakLoadContribution: record['Peak Load Contribution (kW)'].parsedValue ?? location.profileInfo?.peakLoadContribution,
        winterPeakLoad: record['Winter Peak Load (kW)'].parsedValue ?? location.profileInfo?.winterPeakLoad,
        capacityLossFactor: record['Capacity Loss Factor'].parsedValue ?? location.profileInfo?.capacityLossFactor,
        energyLossFactor: record['Energy Loss Factor'].parsedValue ?? location.profileInfo?.energyLossFactor,
        maxLoad: record['Max Load (kW)'].parsedValue ?? location.profileInfo?.maxLoad,
        loadReduction: calcMaxLoadReductionCapability(dto.reductionInfo),
    };

    return { uuid, dto };
}
